import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { CustomModal } from "../components/CustomModal";
import ReservationFloorPlan from "./ReservationFloorPlan.js";
import { api } from "../App";

/* Where is this table? — a read-only plan for one booking.
   The full floor board is unusable on a phone, so instead of shrinking it we
   answer the only question a phone needs answered: point at the table. The
   plan itself is the same component the desktop board draws, so the room
   elements, the welding of grouped tables and the zoom control all come for
   free and cannot drift from the big view. */

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  flex: 1 1 auto;
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  font-size: 13px;
  color: #6b6163;
  flex-shrink: 0;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 4px 9px;
  border-radius: 8px;
  background: #f2f6f4;
  color: #4d5f59;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  b {
    font-weight: 700;
    color: #384742;
  }
`;

const RoomChip = styled(Chip)`
  background: #fff0e9;
  color: #c9552c;

  b {
    color: #a8431f;
  }
`;

const Notice = styled.div`
  padding: 18px 12px;
  text-align: center;
  font-size: 13px;
  color: #8a7f81;
`;

const PlanBox = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1 1 auto;
`;

function parseTableIds(value) {
  if (!value) return [];

  return String(value)
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean)
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id));
}

export default function TableLocatorModal({
  show,
  onHide,
  reservation,
  allTables = [],
  restaurantId,
}) {
  const { t } = useTranslation();
  const { userInfo } = useSelector((state) => state.userLogin);

  const [room, setRoom] = useState(null);
  const [objects, setObjects] = useState([]);
  const [layoutOverrides, setLayoutOverrides] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const authConfig = useMemo(
    () => ({
      headers: userInfo?.token
        ? { Authorization: `Bearer ${userInfo.token}` }
        : {},
    }),
    [userInfo?.token],
  );

  const tableIds = useMemo(
    () => parseTableIds(reservation?.tablesused),
    [reservation?.tablesused],
  );

  // The booking's own tables, then the room they belong to. A booking split
  // across rooms is not a thing the app allows, so the first table decides.
  const bookedTables = useMemo(() => {
    const wanted = new Set(tableIds.map(String));

    return (allTables || []).filter((table) => wanted.has(String(table.id)));
  }, [allTables, tableIds]);

  const roomId = bookedTables[0]?.place_of_table ?? null;

  const roomTables = useMemo(() => {
    if (!roomId) return [];

    return (allTables || []).filter(
      (table) => String(table.place_of_table) === String(roomId),
    );
  }, [allTables, roomId]);

  useEffect(() => {
    if (!show || !restaurantId || !roomId || !reservation?.id) return undefined;

    let cancelled = false;
    setIsLoading(true);

    const loadRoom = api
      .get(`restaurants/${restaurantId}/placeoftable/`, authConfig)
      .then((response) => {
        const rooms = Array.isArray(response.data) ? response.data : [];

        return rooms.find((item) => String(item.id) === String(roomId)) || null;
      })
      .catch(() => null);

    // Elements need layout-edit rights; without them the plan still draws, just
    // without the walls behind the tables.
    const loadObjects = api
      .get(
        `restaurants/${restaurantId}/design-items/?place_of_table=${roomId}`,
        authConfig,
      )
      .then((response) => (Array.isArray(response.data) ? response.data : []))
      .catch(() => []);

    const loadLayout = api
      .get(
        `restaurants/${restaurantId}/reservation-table-layout/${reservation.id}/`,
        authConfig,
      )
      .then((response) => (Array.isArray(response.data) ? response.data : []))
      .catch(() => []);

    Promise.all([loadRoom, loadObjects, loadLayout]).then(
      ([roomData, objectData, layoutData]) => {
        if (cancelled) return;

        setRoom(roomData);
        setObjects(objectData);
        setLayoutOverrides(layoutData);
        setIsLoading(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [show, restaurantId, roomId, reservation?.id, authConfig]);

  const zoneName = room?.place_of_table || "";

  // Every table in the room is drawn, but only this booking's are occupied: the
  // plan reads the tag from the availability row, and passing them as selected
  // as well is what turns them orange.
  const availabilityTables = useMemo(() => {
    const booked = new Set(tableIds.map(String));
    const details = reservation
      ? [
          {
            id: reservation.id,
            full_name: reservation.full_name,
            time: reservation.time,
            estimatedtime: reservation.estimatedtime,
            number_of_people: reservation.number_of_people,
          },
        ]
      : [];

    return roomTables.map((table) => {
      const isBooked = booked.has(String(table.id));

      return {
        number: String(table.id),
        status: isBooked ? "1" : "0",
        reservationIds: isBooked && reservation ? [String(reservation.id)] : [],
        reservationDetails: isBooked ? details : [],
      };
    });
  }, [roomTables, tableIds, reservation]);

  const selectedTables = useMemo(() => {
    if (!zoneName || !reservation) return {};

    return {
      [zoneName]: bookedTables.map((table) => ({
        number: String(table.id),
        status: "1",
        reservationIds: [String(reservation.id)],
        reservationDetails: [
          {
            id: reservation.id,
            full_name: reservation.full_name,
            time: reservation.time,
            estimatedtime: reservation.estimatedtime,
            number_of_people: reservation.number_of_people,
          },
        ],
      })),
    };
  }, [zoneName, reservation, bookedTables]);

  const tableNumbers = bookedTables
    .map((table) => table.table_number)
    .filter((value) => value !== null && value !== undefined)
    .join(", ");

  return (
    <CustomModal
      show={show}
      onHide={onHide}
      fullHeight
      title={t("ui.tableLocator.title", { defaultValue: "Onde fica a mesa" })}
    >
      <Body>
        <Summary>
          {zoneName && (
            <RoomChip title={zoneName}>
              {t("ui.components.tablesWithoutDragging.sala")} <b>{zoneName}</b>
            </RoomChip>
          )}

          {tableNumbers && (
            <Chip title={tableNumbers}>
              {bookedTables.length > 1
                ? t("ui.tableLocator.tables", { defaultValue: "Mesas" })
                : t("ui.tableLocator.table", { defaultValue: "Mesa" })}{" "}
              <b>{tableNumbers}</b>
            </Chip>
          )}

          {reservation?.time && (
            <Chip>
              <b>{reservation.time}</b>
              {reservation?.number_of_people
                ? ` · ${reservation.number_of_people}p`
                : ""}
            </Chip>
          )}
        </Summary>

        {!bookedTables.length ? (
          <Notice>
            {t("ui.tableLocator.noTable", {
              defaultValue: "Esta reserva ainda nao tem mesa atribuida.",
            })}
          </Notice>
        ) : isLoading ? (
          <Notice>
            {t("ui.tableLocator.loading", { defaultValue: "A carregar..." })}
          </Notice>
        ) : !room ? (
          // Without the room's dimensions there is nothing to draw to scale, so
          // say so rather than letting the plan fall back to its empty state.
          <Notice>
            {t("ui.tableLocator.roomUnavailable", {
              defaultValue: "Nao foi possivel carregar a planta da sala.",
            })}
          </Notice>
        ) : (
          <PlanBox>
            <ReservationFloorPlan
              allTables={roomTables}
              zoneName={zoneName}
              availabilityTables={availabilityTables}
              selectedTables={selectedTables}
              layoutOverrides={layoutOverrides}
              room={room}
              objects={objects}
              enableMove={false}
              centerCanvas
              maxZoom={4}
            />
          </PlanBox>
        )}
      </Body>
    </CustomModal>
  );
}
